/**
 * @typedef {Object} RuleViolation
 * @property {string} rule_id
 * @property {string} rule_name
 * @property {'fail'|'warning'|'pass'} severity
 * @property {string} summary
 * @property {string} detail_explanation
 * @property {string[]} affected_waves
 * @property {{label: string, value: number}[]} [fib_values]
 * @property {string} [category]
 */

export const SEVERITY_CONFIG = {
  fail: {
    border: 'border-[#ff3b30]/30',
    bg: 'bg-[#ff3b30]/5',
    text: 'text-[#ff3b30]',
    glow: 'shadow-[0_0_12px_rgba(255,59,48,0.15)]',
    icon: 'XCircle',
    label: 'FAIL',
  },
  warning: {
    border: 'border-[#f5b400]/30',
    bg: 'bg-[#f5b400]/5',
    text: 'text-[#f5b400]',
    glow: 'shadow-[0_0_12px_rgba(245,180,0,0.15)]',
    icon: 'AlertTriangle',
    label: 'WARNING',
  },
  pass: {
    border: 'border-[#00f5d4]/30',
    bg: 'bg-[#00f5d4]/5',
    text: 'text-[#00f5d4]',
    glow: 'shadow-[0_0_12px_rgba(0,245,212,0.15)]',
    icon: 'CheckCircle2',
    label: 'PASS',
  },
};

export const DEMO_VIOLATIONS = [
  {
    rule_id: 'EW-001',
    rule_name: 'Wave 3 Cannot Be Shortest',
    severity: 'fail',
    summary: 'Wave 3 is shorter than both Wave 1 and Wave 5',
    detail_explanation: 'In a standard five-wave impulse sequence, Wave 3 must never be the shortest among Waves 1, 3, and 5. The measured price range of Wave 3 (142 pips) is less than Wave 1 (168 pips) and Wave 5 (155 pips). This fundamentally invalidates the impulse count.',
    affected_waves: ['Wave 1', 'Wave 3', 'Wave 5'],
    fib_values: [
      { label: 'W3/W1', value: 0.845 },
      { label: 'W5/W3', value: 1.091 },
    ],
    category: 'Impulse Rules',
  },
  {
    rule_id: 'EW-002',
    rule_name: 'Wave 4 Overlap Violation',
    severity: 'fail',
    summary: 'Wave 4 retraced into Wave 1 price territory',
    detail_explanation: 'Wave 4 must not enter the price territory of Wave 1 in an impulse wave. The low of Wave 4 at $42,180 overlaps with the high of Wave 1 at $42,350. This overlap of $170 violates the cardinal rule unless this is a diagonal pattern.',
    affected_waves: ['Wave 1', 'Wave 4'],
    fib_values: [
      { label: 'W4 retrace of W3', value: 0.786 },
    ],
    category: 'Impulse Rules',
  },
  {
    rule_id: 'EW-003',
    rule_name: 'Wave 2 Exceeds 100% Retracement',
    severity: 'fail',
    summary: 'Wave 2 retraced beyond the origin of Wave 1',
    detail_explanation: 'Wave 2 must never retrace more than 100% of Wave 1. The corrective wave pulled back 103.2% of Wave 1, meaning the price went below the starting point of the impulse sequence. This invalidates the count entirely.',
    affected_waves: ['Wave 1', 'Wave 2'],
    fib_values: [
      { label: 'W2 retrace of W1', value: 1.032 },
    ],
    category: 'Impulse Rules',
  },
  {
    rule_id: 'EW-004',
    rule_name: 'Fibonacci Extension Warning',
    severity: 'warning',
    summary: 'Wave 5 extension exceeds typical 1.618 ratio',
    detail_explanation: 'Wave 5 extended to 2.41x the length of Waves 1-3 net distance. While not a strict violation, extensions beyond 1.618 suggest the count may need revision or that an extended fifth wave pattern is in play. Monitor for potential blow-off top behavior.',
    affected_waves: ['Wave 5'],
    fib_values: [
      { label: 'W5 ext ratio', value: 2.41 },
    ],
    category: 'Guidelines',
  },
  {
    rule_id: 'EW-005',
    rule_name: 'Corrective Wave Proportion',
    severity: 'warning',
    summary: 'Wave A/B ratio is atypical for a flat correction',
    detail_explanation: 'In a standard flat correction, Wave B typically retraces 90-105% of Wave A. The current Wave B only retraces 72% of Wave A, which is more consistent with a zigzag pattern than a flat. Consider relabeling the corrective structure.',
    affected_waves: ['Wave A', 'Wave B'],
    fib_values: [
      { label: 'B/A ratio', value: 0.72 },
    ],
    category: 'Corrective Rules',
  },
  {
    rule_id: 'EW-006',
    rule_name: 'Impulse Channeling',
    severity: 'pass',
    summary: 'Wave structure fits within parallel channel',
    detail_explanation: 'All pivot points fall within a standard parallel channel drawn from Wave 1 origin through Wave 3 peak and Wave 2 trough. Channel containment of Wave 4 and Wave 5 confirms a clean impulse structure with proper proportionality.',
    affected_waves: ['Wave 1', 'Wave 2', 'Wave 3', 'Wave 4', 'Wave 5'],
    category: 'Guidelines',
  },
  {
    rule_id: 'EW-007',
    rule_name: 'Alternation Principle',
    severity: 'pass',
    summary: 'Waves 2 and 4 show proper alternation',
    detail_explanation: 'Wave 2 is a sharp zigzag correction (62% retrace, completed in 8 bars) while Wave 4 is a shallow flat correction (38% retrace, completed in 14 bars). This alternation in depth, duration, and pattern type satisfies the alternation guideline.',
    affected_waves: ['Wave 2', 'Wave 4'],
    fib_values: [
      { label: 'W2 depth', value: 0.618 },
      { label: 'W4 depth', value: 0.382 },
    ],
    category: 'Guidelines',
  },
  {
    rule_id: 'EW-008',
    rule_name: 'Wave 3 Momentum Confirmation',
    severity: 'pass',
    summary: 'Wave 3 shows strongest momentum readings',
    detail_explanation: 'Volume and momentum indicators confirm Wave 3 as the strongest impulse wave. RSI peaked at 78.4 during Wave 3 versus 65.2 during Wave 1 and 71.1 during Wave 5. MACD histogram was 2.3x larger in Wave 3 than any other impulse wave.',
    affected_waves: ['Wave 3'],
    category: 'Confirmation',
  },
  {
    rule_id: 'EW-009',
    rule_name: 'Diagonal Convergence Check',
    severity: 'warning',
    summary: 'Ending diagonal trendlines may not be converging',
    detail_explanation: 'If the pattern is labeled as an ending diagonal, the upper and lower trendlines should converge toward a terminal point. Currently the trendlines are nearly parallel with only 2 degrees of convergence, which weakens the diagonal interpretation.',
    affected_waves: ['Wave 1', 'Wave 3', 'Wave 5'],
    category: 'Diagonal Rules',
  },
  {
    rule_id: 'EW-010',
    rule_name: 'Time Proportionality',
    severity: 'pass',
    summary: 'Wave durations fall within expected ratios',
    detail_explanation: 'The time ratio of Wave 3 to Wave 1 is 1.59x (near the 1.618 Fibonacci ratio). Wave 5 duration is 0.62x of Wave 3 (near the 0.618 ratio). These time relationships support the wave count\'s structural validity.',
    affected_waves: ['Wave 1', 'Wave 3', 'Wave 5'],
    fib_values: [
      { label: 'W3/W1 time', value: 1.59 },
      { label: 'W5/W3 time', value: 0.62 },
    ],
    category: 'Guidelines',
  },
];
